import type { Feature } from 'geojson'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import {
  TerraDraw,
  TerraDrawFreehandMode,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  type GeoJSONStoreFeatures,
  type TerraDrawEvents,
} from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import {
  aDrawMode,
  aDrawnFeature,
  aMapDrawIsOpen,
  useAtom,
} from '~/atoms'
import {
  clearRememberedDrawnFeatureForBackNavigation,
  rememberDrawnFeatureForBackNavigation,
} from './drawnFeatureBackNavigation'
import { useDrawStyleTokens } from './drawStyleTokens'
import { isStoredCircleFeature } from './googleCircleFeature'
import {
  pickPrimaryFeature,
  polygonContainsLngLat,
  toStorableDrawFeature,
  toTerraDrawFeature,
  toTerraDrawMode,
} from './terraDrawMode'
import { simplifyDrawPolygonFeature } from './polygonGeometry'

function createSelectionFlags() {
  return {
    feature: {
      coordinates: {
        deletable: true,
        draggable: true,
        midpoints: true,
      },
      draggable: true,
      rotateable: true,
    },
  }
}

function createTerraDraw(
  map: google.maps.Map,
  styles: ReturnType<typeof useDrawStyleTokens>,
  showPassiveFeatures: boolean
) {
  const passiveFeatureFillOpacity = showPassiveFeatures ? styles.fillOpacity : 0
  const passiveFeatureOutlineOpacity = showPassiveFeatures ? 1 : 0
  const passiveFeatureOutlineWidth = showPassiveFeatures
    ? styles.outlineWidth
    : 0
  const passivePointOpacity = showPassiveFeatures ? 1 : 0
  const passivePointOutlineOpacity = showPassiveFeatures ? 1 : 0
  const passivePointWidth = showPassiveFeatures ? styles.pointSize : 0

  const passivePolygonStyles = {
    closingPointColor: styles.pointFillColor,
    closingPointOpacity: passivePointOpacity,
    closingPointOutlineColor: styles.pointOutlineColor,
    closingPointOutlineOpacity: passivePointOutlineOpacity,
    closingPointOutlineWidth: styles.pointOutlineWidth,
    closingPointWidth: passivePointWidth,
    coordinatePointColor: styles.pointFillColor,
    coordinatePointOpacity: passivePointOpacity,
    coordinatePointOutlineColor: styles.pointOutlineColor,
    coordinatePointOutlineOpacity: passivePointOutlineOpacity,
    coordinatePointOutlineWidth: styles.pointOutlineWidth,
    coordinatePointWidth: passivePointWidth,
    editedPointColor: styles.pointFillColor,
    editedPointOpacity: passivePointOpacity,
    editedPointOutlineColor: styles.pointOutlineColor,
    editedPointOutlineOpacity: passivePointOutlineOpacity,
    editedPointOutlineWidth: styles.pointOutlineWidth,
    editedPointWidth: passivePointWidth,
    fillColor: styles.fillColor,
    fillOpacity: passiveFeatureFillOpacity,
    outlineColor: styles.outlineColor,
    outlineOpacity: passiveFeatureOutlineOpacity,
    outlineWidth: passiveFeatureOutlineWidth,
    zIndex: styles.passiveFeatureZIndex,
  }

  const passiveFreehandStyles = {
    closingPointColor: styles.pointFillColor,
    closingPointOpacity: passivePointOpacity,
    closingPointOutlineColor: styles.pointOutlineColor,
    closingPointOutlineOpacity: passivePointOutlineOpacity,
    closingPointOutlineWidth: styles.pointOutlineWidth,
    closingPointWidth: passivePointWidth,
    fillColor: styles.fillColor,
    fillOpacity: passiveFeatureFillOpacity,
    outlineColor: styles.outlineColor,
    outlineOpacity: passiveFeatureOutlineOpacity,
    outlineWidth: passiveFeatureOutlineWidth,
    zIndex: styles.passiveFeatureZIndex,
  }

  const selectedStyles = {
    midPointColor: styles.pointFillColor,
    midPointOutlineColor: styles.pointOutlineColor,
    midPointOutlineOpacity: 1,
    midPointOutlineWidth: styles.pointOutlineWidth,
    midPointWidth: styles.midpointSize,
    selectedPolygonColor: styles.fillColor,
    selectedPolygonFillOpacity: styles.fillOpacity,
    selectedPolygonOutlineColor: styles.outlineColor,
    selectedPolygonOutlineOpacity: 1,
    selectedPolygonOutlineWidth: styles.outlineWidth,
    selectionPointColor: styles.pointFillColor,
    selectionPointOutlineColor: styles.pointOutlineColor,
    selectionPointOutlineOpacity: 1,
    selectionPointOutlineWidth: styles.pointOutlineWidth,
    selectionPointWidth: styles.pointSize,
    zIndex: styles.activeFeatureZIndex,
  }

  return new TerraDraw({
    adapter: new TerraDrawGoogleMapsAdapter({
      lib: google.maps,
      map,
    }),
    modes: [
      new TerraDrawSelectMode({
        flags: {
          freehand: createSelectionFlags(),
          polygon: createSelectionFlags(),
        },
        styles: selectedStyles as never,
      }),
      new TerraDrawPolygonMode({
        editable: true,
        styles: passivePolygonStyles as never,
      }),
      new TerraDrawFreehandMode({
        styles: passiveFreehandStyles as never,
      }),
    ],
  })
}

function hasTerraDrawEventElement(map: google.maps.Map) {
  // Terra's Google adapter uses this same private pane selector; revisit if Google changes map DOM structure.
  return Boolean(map.getDiv().querySelector('div[style*="z-index: 3;"]'))
}

function getPrimaryFeatureId(draw: TerraDraw) {
  const feature = pickPrimaryFeature(draw.getSnapshot() as Feature[])

  return feature?.id
}

function getComparableFeatureSignature(feature: Feature | null) {
  if (!feature) return null

  const properties = { ...(feature.properties ?? {}) } as Record<
    string,
    unknown
  >

  delete properties.closingPoint
  delete properties.committedCoordinateCount
  delete properties.coordinatePoint
  delete properties.coordinatePointFeatureId
  delete properties.coordinatePointIds
  delete properties.currentlyDrawing
  delete properties.edited
  delete properties.midPoint
  delete properties.provisionalCoordinateCount
  delete properties.selected
  delete properties.selectionPoint
  delete properties.selectionPointFeatureId
  delete properties.snappingPoint

  return JSON.stringify({
    geometry: feature.geometry,
    properties,
  })
}

function canUseTerraDrawInstance(
  drawRef: React.RefObject<TerraDraw | null>,
  draw: TerraDraw
) {
  return drawRef.current === draw && draw.enabled
}

export function useGoogleTerraDraw() {
  const map = useMap()
  const drawStyles = useDrawStyleTokens()
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const [drawnFeature, setDrawnFeature] = useAtom(aDrawnFeature)
  const [isDrawOpen, setIsDrawOpen] = useAtom(aMapDrawIsOpen)
  const [isReady, setIsReady] = useState(false)
  const drawRef = useRef<TerraDraw | null>(null)
  const drawModeRef = useRef<string | null>(drawMode)
  const previousDrawModeRef = useRef<string | null>(drawMode)
  const isRestoringRef = useRef(false)
  const restoringFrameRef = useRef<number | null>(null)
  const hasPendingFeatureRef = useRef(false)
  const pendingFeatureRef = useRef<Feature | null>(null)
  const showPassiveTerraFeatures =
    drawMode === 'draw_polygon' ||
    drawMode === 'draw_freehand' ||
    drawMode === 'simple_select'

  useLayoutEffect(() => {
    if (
      previousDrawModeRef.current === 'simple_select' &&
      drawMode !== 'simple_select'
    ) {
      const liveFeature = toStorableDrawFeature(
        pickPrimaryFeature(
          (drawRef.current?.getSnapshot() as Feature[]) ?? []
        )
      )
      const nextFeature = simplifyDrawPolygonFeature(
        liveFeature ?? pendingFeatureRef.current
      )

      if (nextFeature) {
        rememberDrawnFeatureForBackNavigation(nextFeature)
        setDrawnFeature(nextFeature)
      }

      hasPendingFeatureRef.current = false
      pendingFeatureRef.current = null
    }

    previousDrawModeRef.current = drawMode
    drawModeRef.current = drawMode
  }, [drawMode, setDrawnFeature])

  const persistPendingFeature = useCallback(() => {
    if (!hasPendingFeatureRef.current) return

    const nextFeature = simplifyDrawPolygonFeature(pendingFeatureRef.current)

    if (nextFeature) {
      rememberDrawnFeatureForBackNavigation(nextFeature)
    }

    setDrawnFeature(nextFeature)
    hasPendingFeatureRef.current = false
    pendingFeatureRef.current = null
  }, [setDrawnFeature])

  const runRestoringMutation = useCallback((mutation: () => void) => {
    if (restoringFrameRef.current !== null) {
      cancelAnimationFrame(restoringFrameRef.current)
    }

    isRestoringRef.current = true
    mutation()
    restoringFrameRef.current = requestAnimationFrame(() => {
      restoringFrameRef.current = null
      isRestoringRef.current = false
    })
  }, [])

  useEffect(() => {
    return () => {
      if (restoringFrameRef.current !== null) {
        cancelAnimationFrame(restoringFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!map) return

    const viewport = map.getDiv().querySelector('.gm-style > div')

    if (!(viewport instanceof HTMLElement)) return

    const isActiveTerraCursorMode =
      drawMode === 'draw_polygon' ||
      drawMode === 'draw_freehand' ||
      drawMode === 'simple_select'

    if (isActiveTerraCursorMode) return

    viewport.classList.remove('terra-draw-google-maps')
    viewport.style.cursor = 'grab'
  }, [drawMode, map])

  useEffect(() => {
    if (!map) {
      setIsReady(false)
      return
    }

    if (hasTerraDrawEventElement(map)) {
      setIsReady(true)
      return
    }

    let frameId: number | null = null

    const checkReady = () => {
      if (hasTerraDrawEventElement(map)) {
        setIsReady(true)
        frameId = null
        return
      }

      frameId = requestAnimationFrame(checkReady)
    }

    checkReady()

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId)
    }
  }, [map])

  useEffect(() => {
    if (!map || !isReady) return

    const draw = createTerraDraw(map, drawStyles, showPassiveTerraFeatures)
    drawRef.current = draw
    draw.start()

    const handleChange = () => {
      if (isRestoringRef.current) {
        return
      }

      const snapshot = draw.getSnapshot() as Feature[]
      const nextPrimary = toStorableDrawFeature(pickPrimaryFeature(snapshot))

      hasPendingFeatureRef.current = true
      pendingFeatureRef.current = nextPrimary

      if (!nextPrimary && snapshot.length === 0) {
        clearRememberedDrawnFeatureForBackNavigation()
        hasPendingFeatureRef.current = false
        pendingFeatureRef.current = null
        setDrawnFeature(null)
        setDrawMode(null)
      }
    }

    const handleFinish = () => {
      persistPendingFeature()

      if (drawModeRef.current === 'simple_select') {
        return
      }

      setDrawMode(null)
      setIsDrawOpen(false)
    }

    const handleSelect = () => {
      setDrawMode('simple_select')
      setIsDrawOpen(true)
    }

    const events: [TerraDrawEvents, () => void][] = [
      ['change', handleChange],
      ['finish', handleFinish],
      ['select', handleSelect],
    ]

    events.forEach(([eventName, handler]) => {
      draw.on(eventName, handler)
    })

    return () => {
      events.forEach(([eventName, handler]) => {
        draw.off(eventName, handler)
      })

      draw.stop()
      drawRef.current = null
    }
  }, [
    drawStyles,
    isReady,
    map,
    persistPendingFeature,
    setDrawMode,
    setDrawnFeature,
    setIsDrawOpen,
    showPassiveTerraFeatures,
  ])
  useEffect(() => {
    const draw = drawRef.current

    if (!draw) return

    const shouldShowTerraFeature =
      drawMode === 'draw_polygon' ||
      drawMode === 'draw_freehand' ||
      drawMode === 'simple_select'

    if (isStoredCircleFeature(drawnFeature) || drawMode === 'draw_circle') {
      const currentFeature = pickPrimaryFeature(draw.getSnapshot() as Feature[])

      if (currentFeature) {
        runRestoringMutation(() => {
          draw.clear()
        })
      }

      return
    }

    const currentFeature = toStorableDrawFeature(
      pickPrimaryFeature(draw.getSnapshot() as Feature[])
    )

    if (!shouldShowTerraFeature) {
      if (!currentFeature) return

      runRestoringMutation(() => {
        draw.clear()
      })
      return
    }

    if (!drawnFeature) {
      if (!currentFeature) return

      runRestoringMutation(() => {
        draw.clear()
      })
      return
    }

    const nextFeature = toTerraDrawFeature(drawnFeature)
    if (!nextFeature) return

    if (
      getComparableFeatureSignature(currentFeature) ===
      getComparableFeatureSignature(toStorableDrawFeature(nextFeature))
    ) {
      return
    }

    runRestoringMutation(() => {
      draw.clear()
      draw.addFeatures([nextFeature as GeoJSONStoreFeatures])
    })
  }, [drawMode, drawnFeature, isDrawOpen, runRestoringMutation])

  useEffect(() => {
    const draw = drawRef.current

    if (!draw) return

    const nextMode =
      !isDrawOpen || drawMode === 'draw_circle'
        ? 'static'
        : drawMode
          ? toTerraDrawMode(drawMode)
          : 'static'

    if (draw.getMode() === nextMode) return

    if (nextMode === 'static') {
      runRestoringMutation(() => {
        draw.setMode(nextMode)
      })
      return
    }

    draw.setMode(nextMode)
  }, [drawMode, drawnFeature, isDrawOpen, runRestoringMutation])

  useEffect(() => {
    const draw = drawRef.current

    if (
      !draw ||
      drawMode !== 'simple_select' ||
      !drawnFeature ||
      isStoredCircleFeature(drawnFeature)
    ) {
      return
    }

    const featureId = getPrimaryFeatureId(draw)

    if (!featureId) return

    let selectModeFrameId: number | null = null
    let selectFeatureFrameId: number | null = null

    selectModeFrameId = requestAnimationFrame(() => {
      if (!canUseTerraDrawInstance(drawRef, draw)) return

      if (draw.getMode() !== 'select') {
        draw.setMode('select')
      }

      selectFeatureFrameId = requestAnimationFrame(() => {
        if (!canUseTerraDrawInstance(drawRef, draw)) return

        draw.selectFeature(featureId)
      })
    })

    return () => {
      if (selectModeFrameId !== null) {
        cancelAnimationFrame(selectModeFrameId)
      }
      if (selectFeatureFrameId !== null) {
        cancelAnimationFrame(selectFeatureFrameId)
      }
    }
  }, [drawMode, drawnFeature])

  useEffect(() => {
    const draw = drawRef.current

    if (
      !map ||
      !draw ||
      !drawnFeature ||
      drawMode ||
      isStoredCircleFeature(drawnFeature)
    )
      return

    const handlePotentialSelect = (
      latLng: google.maps.LatLng | null | undefined
    ) => {
      if (!latLng) return

      const lngLat = {
        lat: latLng.lat(),
        lng: latLng.lng(),
      }

      if (!polygonContainsLngLat(drawnFeature, lngLat)) return

      const featureId = getPrimaryFeatureId(draw)

      if (!featureId) return

      setIsDrawOpen(true)
      setDrawMode('simple_select')

      requestAnimationFrame(() => {
        if (draw.getMode() !== 'select') {
          draw.setMode('select')
        }

        requestAnimationFrame(() => {
          draw.selectFeature(featureId)
        })
      })
    }

    const mapClickListener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        handlePotentialSelect(event.latLng)
      }
    )

    const dataClickListener = map.data.addListener(
      'click',
      (event: google.maps.Data.MouseEvent) => {
        handlePotentialSelect(event.latLng)
      }
    )

    return () => {
      mapClickListener.remove()
      dataClickListener.remove()
    }
  }, [drawMode, drawnFeature, map, setDrawMode, setIsDrawOpen])

  return useMemo(
    () => ({
      canDraw: Boolean(map) && isReady,
      clearDrawing: () => {
        const draw = drawRef.current

        if (!draw) return

        hasPendingFeatureRef.current = false
        pendingFeatureRef.current = null
        runRestoringMutation(() => {
          draw.clear()
          draw.setMode('static')
        })
        setDrawnFeature(null)
        setDrawMode(null)
      },
    }),
    [isReady, map, runRestoringMutation, setDrawMode, setDrawnFeature]
  )
}
